import logo from 'assets/header-prevencor-xx.png';
import md5 from 'md5';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import fetchImageBase64 from 'utils/helpers';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

function sectionTitle(text) {
  return {
    text,
    bold: true,
    decoration: 'underline',
    margin: [0, 6, 0, 2],
  };
}

function textLine(label, value) {
  if (value === null || value === undefined || value === '') return null;
  return {
    text: [{ text: `${label}: `, bold: true }, String(value)],
    margin: [0, 1, 0, 1],
  };
}

function listSection(title, items, keys = ['DESCRICAO', 'conclusoes', 'CONTRASEG']) {
  if (!Array.isArray(items) || !items.length) return [];

  const lines = items
    .map((item) => {
      const first = keys.map((k) => String(item?.[k] ?? '').trim()).find((v) => v.length > 0);
      return first || '';
    })
    .filter(Boolean);

  if (!lines.length) return [];

  return [sectionTitle(title), ...lines.map((line) => ({ text: line, margin: [10, 0, 0, 1] }))];
}

// Like listSection but without emitting the title (use when the title is already rendered separately)
function listItems(items, keys = ['DESCRICAO', 'conclusoes', 'CONTRASEG']) {
  if (!Array.isArray(items) || !items.length) return [];

  const lines = items
    .map((item) => {
      const first = keys.map((k) => String(item?.[k] ?? '').trim()).find((v) => v.length > 0);
      return first || '';
    })
    .filter(Boolean);

  return lines.map((line) => ({ text: line, margin: [10, 0, 0, 1] }));
}

function septoSection(title, items, descKey, medidaKey = 'MEDIDA') {
  if (!Array.isArray(items) || !items.length) return [];

  const lines = items
    .map((item) => {
      const desc = String(item?.[descKey] ?? '').trim();
      const medida = item?.[medidaKey] != null ? ` - Valor: ${item[medidaKey]} cm` : '';
      return desc ? `${desc}${medida}` : '';
    })
    .filter(Boolean);

  if (!lines.length) return [];

  return [sectionTitle(title), ...lines.map((line) => ({ text: line, margin: [10, 0, 0, 1] }))];
}

export async function generatePDFCongenito({ eco }) {
  if (!eco) return;

  const logoBase64 = await fetchImageBase64(logo);
  const hashAssinatura = md5(JSON.stringify(eco));

  const content = [
    {
      text: 'Laudo de Ecocardiograma Congenito',
      bold: true,
      decoration: 'underline',
      alignment: 'center',
      margin: [0, 0, 0, 10],
    },
    {
      text: [{ text: 'Paciente: ', bold: true }, `${eco.CODPAC || '-'} ${eco.NOMEPAC || '-'}`],
      margin: [0, 0, 0, 1],
    },
    {
      columns: [
        {
          text: [
            { text: 'Sexo: ', bold: true },
            String(eco.Sexo || '-'),
            { text: '   Idade: ', bold: true },
            `${eco.idadePAC ?? '-'} anos`,
          ],
          width: '*',
        },
        {
          text: [{ text: 'Data do Exame: ', bold: true }, String(eco.dataEcoFmt || '-')],
          width: 'auto',
          alignment: 'right',
        },
      ],
      margin: [0, 1, 0, 1],
    },
    {
      columns: [
        {
          text: [{ text: 'Medico que Realizou: ', bold: true }, `Dr.(a): ${eco.Realizou || '-'}`],
          width: '*',
        },
        {
          text: String(eco.CODECO || ''),
          bold: true,
          width: 'auto',
          alignment: 'right',
        },
      ],
      margin: [0, 1, 0, 1],
    },
    {
      text: [{ text: 'Medico que Solicitou: ', bold: true }, `Dr.(a): ${eco.Solicitou || '-'}`],
      margin: [0, 1, 0, 1],
    },
    {
      text: [{ text: 'Convenio: ', bold: true }, String(eco.SIGLACONV || '-')],
      margin: [0, 1, 0, 6],
    },
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }],
      margin: [0, 0, 0, 6],
    },

    ...listSection('Observacoes', eco.obs),

    sectionTitle('Diametros e Funcao Sistolica do Ventriculo Esquerdo'),
    textLine('Septo', eco.SEPTO != null ? `${Number(eco.SEPTO).toFixed(1)} cm` : ''),
    textLine('PPVE', eco.PPVE != null ? `${Number(eco.PPVE).toFixed(1)} cm` : ''),
    textLine('Diametro Diastolico', eco.DD != null ? `${Number(eco.DD).toFixed(1)} cm` : ''),
    textLine('Diametro Sistolico', eco.DS != null ? `${Number(eco.DS).toFixed(1)} cm` : ''),
    textLine(
      'Fracao de Ejecao (Simpson)',
      eco.FRAEJECBIPLA != null ? Number(eco.FRAEJECBIPLA).toFixed(2) : ''
    ),
    textLine('Massa do VE', eco.massaVE != null ? `${eco.massaVE} g/m2` : ''),

    ...listSection('Achados Descritivos do VE', eco.achados),
    ...listSection('Contracao Segmentar', eco.contraRegiao, ['CONTRASEG']),

    sectionTitle('Atrio Esquerdo e Valva Mitral'),
    textLine(
      'Diametro do Atrio Esquerdo',
      eco.DAE != null ? `${Number(eco.DAE).toFixed(1)} cm` : ''
    ),
    textLine(
      'Volume do AE',
      eco.VOL_AE != null ? `${Number(eco.VOL_AE).toFixed(2)} cc/m2 (ate 34)` : ''
    ),
    ...listSection('Atrio Esquerdo', eco.atrioEsq),
    ...listSection('Valva Mitral', eco.valvaMitral),

    sectionTitle('Valva Aortica'),
    textLine(
      'Diametro da Raiz Aortica',
      eco.DAVAO != null ? `${Number(eco.DAVAO).toFixed(1)} cm` : ''
    ),
    ...listItems(eco.valvaAO),
    ...listSection('Aorta', eco.aorta),

    sectionTitle('Atrio Direito'),
    ...listItems(eco.atrioDir),

    sectionTitle('Veia Cava Inferior'),
    ...listItems(eco.veiaCavaInf),

    sectionTitle('Ventriculo Direito'),
    ...listItems(eco.ventriDir),

    sectionTitle('Valva Tricuspide'),
    ...listItems(eco.valvaTricu),

    sectionTitle('Valva Pulmonar'),
    textLine(
      'Diametro do Anel Pulmonar',
      eco.DAVP != null ? `${Number(eco.DAVP).toFixed(1)} cm` : ''
    ),
    ...listItems(eco.valvaPulmo),

    ...listSection('Pericardio', eco.pericardio),

    // Congenito-specific section
    sectionTitle('Estudo Congenito'),
    ...listSection('Situs', eco.situs),
    ...septoSection('Septo Interatrial', eco.septoIA, 'DESCRICAO'),
    ...septoSection('Septo Interventricular', eco.septoIV, 'septoIV'),
    ...(eco.GVEVD != null && eco.estimativaPSVDCong != null
      ? [
          {
            text: `Estimativa da PSAP: ${Number(eco.estimativaPSVDCong).toFixed(1)} mm Hg   Relacao QP/QS: ${eco.relacaoPSCong ?? '-'}`,
            margin: [10, 0, 0, 1],
          },
        ]
      : []),
    ...listSection('Concordancia Atrioventricular', eco.concordAV),
    ...listSection('Eco Venoso Sistemico', eco.ecoVS),
    ...listSection('Concordancia Ventriculoarterial', eco.concordVA),
    ...listSection('Arteria Pulmonar', eco.artP),
    ...listSection('Arteria Coronaria', eco.artC),
    ...listSection('Arco Aortico', eco.arcoAO),
    ...listSection('Conexao', eco.conexao),
    ...listSection('Estrutura', eco.estrutura),

    // Doppler sections
    sectionTitle('Doppler Mitral'),
    textLine(
      'Area Valva Mitral',
      eco.AVM != null ? `${Number(eco.AVM).toFixed(1)} cm2 ${eco.valorAVM ?? ''}` : ''
    ),
    textLine('Gradiente Medio', eco.GMVM != null ? `${eco.GMVM} mmHg` : ''),
    textLine(
      'Relacao ao Collor IM/AE',
      eco.RELIMAECLL != null && !Number.isNaN(Number(eco.RELIMAECLL))
        ? `${eco.RELIMAECLL}  ${eco.valorRelacaoIMAECLL ?? ''}`
        : ''
    ),
    textLine(
      'Orificio Regurgitacao Mitral (PISA)',
      eco.orificioRM != null
        ? `${Number(eco.orificioRM).toFixed(2)} cm2  ${eco.valorPISA ?? ''}`
        : ''
    ),
    textLine('Volume Regurgitacao Mitral', eco.VOLUME_VM != null ? `${eco.VOLUME_VM} cc` : ''),
    textLine('Diametro do Anel Mitral', eco.DAM != null ? `${eco.DAM} cm` : ''),
    textLine(
      'Fracao da Regurgitacao Mitral',
      eco.FRM != null && !Number.isNaN(Number(eco.FRM))
        ? `${eco.FRM} % (Normal ou Trivial < 20%)`
        : ''
    ),
    ...listItems(eco.refVM),

    sectionTitle('Doppler Aortico'),
    textLine(
      'Area Valva Aortica',
      eco.AREA_AO != null ? `${Number(eco.AREA_AO).toFixed(2)} cm2 ${eco.valorAVAO ?? ''}` : ''
    ),
    textLine(
      'Fracao da Regurgitacao Aortica',
      eco.FRA != null && !Number.isNaN(Number(eco.FRA))
        ? `${eco.FRA} % (Normal ou Trivial < 20%)`
        : ''
    ),
    textLine('Gradiente Medio da Valva Aortica', eco.GMVAO != null ? `${eco.GMVAO} mmHg` : ''),
    textLine('Gradiente Medio Via de Saida do VE', eco.GMVSVE != null ? `${eco.GMVSVE} mmHg` : ''),
    textLine(
      'Relacao ao Collor da I Aortica',
      eco.RELIAOCLL != null && !Number.isNaN(Number(eco.RELIAOCLL))
        ? `${eco.RELIAOCLL}  ${eco.valorRelacaoIAOCLL ?? ''}`
        : ''
    ),
    ...listItems(eco.refVAO),

    sectionTitle('Doppler Tricuspide'),
    textLine('Gradiente Medio da Valva Tricuspide', eco.GMVT != null ? `${eco.GMVT} mmHg` : ''),
    textLine(
      'Estimativa da Pressao Sistolica da AP',
      eco.estimativaPSVD != null ? `${eco.estimativaPSVD} mmHg` : ''
    ),
    ...listItems(eco.refVT),

    sectionTitle('Doppler Pulmonar'),
    textLine('Gradiente Medio da Valva Pulmonar', eco.GMVP != null ? `${eco.GMVP} mmHg` : ''),
    ...listItems(eco.refVP),

    ...(eco.GMPM != null || (Array.isArray(eco.refluxoPM) && eco.refluxoPM.length > 0)
      ? [
          sectionTitle('Protese Mitral'),
          textLine('Gradiente Medio da Protese Mitral', eco.GMPM != null ? `${eco.GMPM} mmHg` : ''),
          ...listSection('Refluxo Protese Mitral', eco.refluxoPM, ['refluxoPM']),
        ]
      : []),

    ...(eco.GMPAO != null || (Array.isArray(eco.refluxoPAO) && eco.refluxoPAO.length > 0)
      ? [
          sectionTitle('Protese Aortica'),
          textLine(
            'Gradiente Medio da Protese Aortica',
            eco.GMPAO != null ? `${eco.GMPAO} mmHg` : ''
          ),
          ...listSection('Refluxo Protese Aortica', eco.refluxoPAO, ['refluxoPAO']),
        ]
      : []),

    ...(eco.GMPT != null || (Array.isArray(eco.refluxoPT) && eco.refluxoPT.length > 0)
      ? [
          sectionTitle('Protese Tricuspide'),
          textLine(
            'Gradiente Medio da Protese Tricuspide',
            eco.GMPT != null ? `${eco.GMPT} mmHg` : ''
          ),
          ...listSection('Refluxo Protese Tricuspide', eco.refluxoPT, ['refluxoPT']),
        ]
      : []),

    ...(eco.GMPP != null || (Array.isArray(eco.refluxoPP) && eco.refluxoPP.length > 0)
      ? [
          sectionTitle('Protese Pulmonar'),
          textLine(
            'Gradiente Medio da Protese Pulmonar',
            eco.GMPP != null ? `${eco.GMPP} mmHg` : ''
          ),
          ...listSection('Refluxo Protese Pulmonar', eco.refluxoPP, ['refluxoPP']),
        ]
      : []),

    ...(String(eco.OBSERVACOES || '').trim()
      ? [
          sectionTitle('Observacoes Finais'),
          { text: String(eco.OBSERVACOES), margin: [10, 0, 0, 1] },
        ]
      : []),

    sectionTitle('Conclusoes'),
    ...listSection('Valvulopatias', eco.valvulopatia, ['valvulopatia']),
    ...listSection('Proteses', eco.protese, ['protese']),
    ...listSection('Contracao Segmentar (Conclusao)', eco.contraSeg, ['contraSeg']),
    ...listItems(eco.conclusoes, ['conclusoes']),

    {
      text: 'Conferido e assinado eletronicamente por:',
      italics: true,
      alignment: 'center',
      margin: [0, 20, 0, 3],
    },
    {
      text: `Dr(a).: ${eco.Realizou || '-'}`,
      bold: true,
      alignment: 'center',
      margin: [0, 0, 0, 3],
    },
    {
      text: `Assinatura Digital: ${hashAssinatura}`,
      bold: true,
      italics: true,
      alignment: 'center',
      fontSize: 9,
      margin: [0, 0, 0, 12],
    },
  ].filter(Boolean);

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 90, 40, 68],
    header: function () {
      return {
        stack: [
          {
            image: logoBase64,
            width: 460,
            alignment: 'center',
            margin: [0, 6, 0, 2],
          },
          {
            text: String(eco.CODECO || ''),
            bold: true,
            alignment: 'right',
            fontSize: 10,
            margin: [40, 0, 40, 0],
          },
        ],
      };
    },
    footer: function (currentPage, pageCount) {
      return [
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }],
          margin: [40, 2, 40, 0],
        },
        {
          text: 'Rua Claudio Manuel da Costa, 56 - Fone (14) 3402-2700/Fax: (14) 3402-2705 - Jd Maria Isabel - CEP 17.515-460 - Marilia/SP',
          alignment: 'center',
          fontSize: 7,
          margin: [40, 2, 40, 0],
        },
        {
          text: 'prevencor@unimedmarilia.com.br',
          alignment: 'center',
          fontSize: 7,
          margin: [40, 0, 40, 0],
        },
        {
          text: `Pag: ${currentPage} de ${pageCount}`,
          alignment: 'center',
          fontSize: 8,
          bold: true,
          margin: [40, 1, 40, 0],
        },
      ];
    },
    content,
    defaultStyle: { fontSize: 10 },
  };

  pdfMake.createPdf(docDefinition).open();
}
